// =============================================================================
// NAD+ Hero Section Component
// =============================================================================

class NadHeroSection {
  private section: HTMLElement;
  private observers: Map<HTMLElement, IntersectionObserver> = new Map();
  private scrollArrowHandlers: Array<{ element: HTMLElement; handler: (e: Event) => void }> = [];

  constructor(section: HTMLElement) {
    this.section = section;
    this.init();
  }

  private init(): void {
    this.setupScrollAnimation();
    this.setupScrollDownArrow();
    console.log('NAD Hero Section initialized');
  }

  /**
   * Setup scroll animation using Intersection Observer
   */
  private setupScrollAnimation(): void {
    if (!this.section) return;

    // Create intersection observer for the main section
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add animation class when section becomes visible
            entry.target.classList.add('animate-in');
            sectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sectionObserver.observe(this.section);
    this.observers.set(this.section, sectionObserver);

    // Setup individual element animations
    this.setupElementAnimations();
  }

  /**
   * Setup scroll down arrow functionality
   */
  private setupScrollDownArrow(): void {
    // Get the section ID to find both mobile and desktop arrows
    const sectionId = this.section.id;
    
    // Find both mobile (inside section) and desktop (outside section) arrows
    const mobileArrows = this.section.querySelectorAll('.nad-hero-section__scroll-down-arrow-icon');
    const desktopArrows = document.querySelectorAll(`#${sectionId} ~ .nad-hero-section__scroll-down-arrow .nad-hero-section__scroll-down-arrow-icon`);
    
    // Combine both sets of arrows
    const allArrows = [...mobileArrows, ...desktopArrows];
    
    allArrows.forEach((arrow) => {
      const handler = (e: Event) => {
        e.preventDefault();
        this.scrollToNextSection();
      };
      
      arrow.addEventListener('click', handler);
      this.scrollArrowHandlers.push({ element: arrow as HTMLElement, handler });
    });
  }

  /**
   * Scroll down by the height of the hero section
   */
  private scrollToNextSection(): void {
    const currentSection = this.section;
    const heroHeight = currentSection.offsetHeight;
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    const targetPosition = currentScrollPosition + heroHeight;
    
    // Smooth scroll down by the hero section height
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }


  /**
   * Setup animations for individual elements
   */
  private setupElementAnimations(): void {
    const elements = this.section.querySelectorAll('.nad-hero-section__text-block, .nad-hero-section__info-block');
    
    elements.forEach((element, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              
              // Add animation-complete class after animation finishes
              const animationDuration = 800; // 0.8s in milliseconds
              const delay = this.getAnimationDelay(element as HTMLElement);
              
              setTimeout(() => {
                entry.target.classList.add('animation-complete');
              }, animationDuration + delay);
              
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -30px 0px'
        }
      );

      observer.observe(element);
      this.observers.set(element as HTMLElement, observer);
    });
  }

  /**
   * Get animation delay for staggered animations
   */
  private getAnimationDelay(element: HTMLElement): number {
    const parent = element.parentElement;
    if (!parent) return 0;
    
    const siblings = Array.from(parent.children);
    const index = siblings.indexOf(element);
    
    // Return delay based on element type and index
    if (element.classList.contains('nad-hero-section__info-block')) {
      return index * 100; // 100ms delay between info blocks (matches CSS delays)
    }
    
    return 0;
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    // Cleanup intersection observers
    this.observers.forEach((observer, element) => {
      observer.disconnect();
      element.classList.remove('animate-in');
    });
    this.observers.clear();

    // Cleanup scroll arrow event listeners
    this.scrollArrowHandlers.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
    });
    this.scrollArrowHandlers = [];
  }

  /**
   * Static method to initialize all sections
   */
  public static initializeAll(): NadHeroSection[] {
    const sections = document.querySelectorAll<HTMLElement>('.nad-hero-section');
    return Array.from(sections).map(section => new NadHeroSection(section));
  }

  /**
   * Static method to destroy a section instance
   */
  public static destroySection(section: HTMLElement): void {
    const instance = (section as any).__nadHeroSection;
    if (instance && instance.destroy) {
      instance.destroy();
    }
  }
}

// Export for initialization from main file
export { NadHeroSection };
